// eslint-disable-next-line import/no-cycle
import { useGlobalStore } from '@/stores/globalStore';
import http from './http';
import useErrorHandling from './useErrorHandling';

export default () => {
  const { handleServerErrors } = useErrorHandling();

  let pendingTokenPromise = null;

  const resetFetchingState = () => {
    const globalStore = useGlobalStore();
    globalStore.fetchingToken = false;
    pendingTokenPromise = null;
  };

  const getToken = async () => http
      .get('/token')
      .then((response) => {
        const token = response.data.bearer_token;
        const globalStore = useGlobalStore();
        sessionStorage.setItem('token', token);
        globalStore.token = token;

        return token;
      })
      .catch((err) => {
        handleServerErrors(err);
        throw err;
      });

  const waitUntilTokenRetrieved = async (timeoutMs = 10000) => {
    const startTime = Date.now();

    return new Promise((resolve, reject) => {
      const checkInterval = setInterval(() => {
        const sessionToken = sessionStorage.getItem('token');
        const globalStore = useGlobalStore();
        const storeToken = globalStore.token;
        const availableToken = sessionToken || storeToken;

        if (availableToken) {
          if (!sessionToken && storeToken) {
            sessionStorage.setItem('token', storeToken);
          }
          if (!storeToken && sessionToken) {
            globalStore.token = sessionToken;
          }

          resolve(availableToken);
          clearInterval(checkInterval);
          return;
        }

        // Check for timeout
        if (Date.now() - startTime > timeoutMs) {
          clearInterval(checkInterval);
          reject(new Error('Token retrieval timeout'));
        }
      }, 100);
    });
  };

  const checkAndSyncExistingToken = () => {
    const globalStore = useGlobalStore();
    const sessionToken = sessionStorage.getItem('token');
    const storeToken = globalStore.token;
    const existingToken = sessionToken || storeToken;

    if (!sessionToken && storeToken) {
      sessionStorage.setItem('token', storeToken);
    }

    if (!storeToken && sessionToken) {
      globalStore.token = sessionToken;
    }

    return existingToken;
  };

  const handlePendingTokenFetch = async () => {
    if (!pendingTokenPromise) return null;

    try {
      return await pendingTokenPromise;
    } catch (error) {
      resetFetchingState();
      throw error;
    }
  };

  const handleWaitForToken = async () => {
    try {
      return await waitUntilTokenRetrieved();
    } catch (error) {
      console.warn('Token wait timeout, attempting fresh fetch');
      resetFetchingState();
      return null;
    }
  };

  const performTokenFetch = async () => {
    const globalStore = useGlobalStore();
    globalStore.fetchingToken = true;

    pendingTokenPromise = (async () => {
      try {
        await http.get('/csrf-cookie').catch((err) => {
          handleServerErrors(err);
          throw err;
        });

        const authToken = await getToken();

        return authToken;
      } finally {
        resetFetchingState();
      }
    })();

    return pendingTokenPromise;
  };

  // Fetch the auth token
  // This function is used to fetch the auth token from the server
  // It checks for an existing token and syncs the storage (pinia store and session storage)
  // If there's a pending promise, it waits for it
  // If there's no pending promise, it performs a fresh fetch
  const fetchAuthToken = async () => {
    const globalStore = useGlobalStore();
    const existingToken = checkAndSyncExistingToken();

    if (existingToken) {
      return existingToken;
    }

    if (globalStore.fetchingToken) {
      const pendingResult = await handlePendingTokenFetch();
      if (pendingResult) {
        return pendingResult;
      }

      const waitResult = await handleWaitForToken();
      if (waitResult) {
        return waitResult;
      }
    }

    return performTokenFetch();
  };

  const clearAuthToken = () => {
    sessionStorage.removeItem('token');
    const globalStore = useGlobalStore();
    globalStore.token = null;
    resetFetchingState();
  };

  const setAuthToken = (token) => {
    sessionStorage.setItem('token', token);
    const globalStore = useGlobalStore();
    globalStore.token = token;
  };

  return {
    fetchAuthToken,
    waitUntilTokenRetrieved,
    clearAuthToken,
    setAuthToken,
  };
};
